import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { isBalancedPublicRoute } from "./lib/balanced-public-route";
import "./index.css";
import { registerPwaSw } from "./lib/pwa-sw";

function isKnownGafaSdkTeardownError(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): boolean {
  const originalMessage =
    hint.originalException instanceof Error
      ? hint.originalException.message
      : typeof hint.originalException === "string"
        ? hint.originalException
        : "";
  const messages = [
    originalMessage,
    event.message ?? "",
    ...(event.exception?.values?.map((value) => value.value ?? "") ?? []),
  ].join(" ");
  const isKnownTeardownMessage =
    /removeChild.*(?:not a child|not.*child)/i.test(messages) ||
    /node to be removed is not a child/i.test(messages) ||
    /should not already be working/i.test(messages);
  const comesFromGafaSdk = Boolean(
    event.exception?.values?.some((value) =>
      value.stacktrace?.frames?.some((frame) =>
        frame.filename?.includes("gafa-sdk.bundle"),
      ),
    ),
  );

  return isKnownTeardownMessage && comesFromGafaSdk;
}

if (import.meta.env.DEV) {
  registerPwaSw();
}

const sentryEnabled = import.meta.env.PROD &&
  import.meta.env.MODE === "production" &&
  (window as unknown as { __BUQ_PRODUCTION__?: boolean }).__BUQ_PRODUCTION__ === true &&
  Boolean(import.meta.env.VITE_SENTRY_DSN);

if (sentryEnabled) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: "production",
    beforeSend(event, hint) {
      return isKnownGafaSdkTeardownError(event, hint) ? null : event;
    },
  });
}

const SentryErrorBoundary = sentryEnabled
  ? Sentry.ErrorBoundary
  : ({ children }: { children: React.ReactNode }) => <>{children}</>;

const domainSlug = (window as unknown as Record<string, unknown>).__BESPOKE_SLUG__ as string | undefined;
const app = isBalancedPublicRoute(window.location.pathname, domainSlug)
  ? import("./pages/balanced/public-app")
  : import("./App");

void app.then(({ default: App }) => {
  createRoot(document.getElementById("root")!).render(
    <SentryErrorBoundary><App /></SentryErrorBoundary>,
  );
}).catch(error => {
  if (sentryEnabled) Sentry.captureException(error);
  console.error("No se pudo cargar la aplicación", error);
  const root = document.getElementById("root")!;
  const panel = document.createElement("div");
  panel.setAttribute("role", "alert");
  panel.style.cssText = "padding:3rem;text-align:center;font-family:system-ui";
  panel.textContent = "No se pudo cargar el sitio. Comprueba tu conexión e inténtalo de nuevo. ";
  const retry = document.createElement("button");
  retry.type = "button";
  retry.textContent = "Volver a cargar";
  retry.onclick = () => window.location.reload();
  panel.append(retry);
  root.replaceChildren(panel);
});
